import { fabric } from 'fabric';

import { contrast, extractPalette, rgba } from './colors';
import { loadFont } from './fonts';
import { iconSvg } from './icons';
import {
    Anchor,
    Box,
    fitTextWidth,
    loadImageElement,
    makeImage,
    makeLine,
    makeLogo,
    makePill,
    makeRect,
    makeScrim,
    makeSvgGroup,
    makeText,
    PillOpts,
    setLocked,
    tag,
    textWidth,
    TextOpts,
} from './objects';
import type { ArtboardSize, BrandInfo, FontPair, Translate } from './types';

/**
 * Templates, drawn from code.
 *
 * Each one is a function of the artboard size, the brand and the pictures at
 * hand, so the same template lays out correctly on a square post, a 4:5
 * portrait, a story and a landscape banner, in the vendor's colours and type.
 *
 * The rules they share are the ones an agency would apply: one margin (7.4% of
 * the short side) everywhere, a type scale tied to the frame, one display face
 * and one text face, letter-spaced caps only for small labels, scrims instead
 * of boxes behind type on photos, and nothing decorative that does not carry
 * information.
 *
 * Every object is tagged with the colour/font role it plays, which is what lets
 * "Apply brand" recolour a finished design later.
 */
export interface TemplateContext {
    art: ArtboardSize;
    brand: BrandInfo;
    pair: FontPair;
    t: Translate;
    /** Pictures to place: [0] is the hero (the generated image), the rest fill secondary slots. */
    images: string[];
    /** Use the brand's own typeface for headings when it has one. */
    useBrandFont: boolean;
    /**
     * Real copy for the template's slots (the agent's words). When given, every
     * slot takes its text from here, slots without copy are left out, and the
     * template's sample copy — "-30%", "Ends Sunday", made-up specs — is never
     * shown, because a claim the vendor did not make must not reach a post.
     */
    copy?: TemplateCopy;
}

export interface TemplateCopy {
    eyebrow?: string;
    headline?: string;
    sub?: string;
    cta?: string;
    spec?: string;
}

type Slot = keyof TemplateCopy | 'keep';

/**
 * Which slot each template text fills. Unlisted keys are sample copy and are
 * dropped when real copy is supplied; 'keep' marks neutral labels (Before /
 * After, Swipe, Link in bio) that read right on any post.
 */
const SLOTS: Record<string, Slot> = {
    BRAND: 'keep',
    NA_EYEBROW: 'eyebrow', NA_TITLE: 'headline', NA_SUB: 'sub',
    COL_EYEBROW: 'eyebrow', COL_TITLE: 'headline', COL_META: 'sub', COL_SPEC: 'spec',
    SP_EYEBROW: 'eyebrow', SP_TITLE: 'headline', SP_DESC: 'sub', SP_CHIP1: 'spec',
    SALE_EYEBROW: 'eyebrow', SALE_TITLE: 'headline', SALE_SUB: 'sub', SALE_CTA: 'cta',
    ST_EYEBROW: 'eyebrow', ST_TITLE: 'headline', ST_SUB: 'sub', ST_CTA: 'cta', ST_LINK: 'keep',
    BA_BEFORE: 'keep', BA_AFTER: 'keep', BA_CAPTION: 'headline',
    CO_EYEBROW: 'eyebrow', CO_TITLE: 'headline', CO_C1: 'spec', CO_C2: 'sub', CO_C3: 'cta',
    TIP_EYEBROW: 'eyebrow', TIP_TITLE: 'headline', TIP_BODY: 'sub', TIP_SWIPE: 'keep',
    Q_TEXT: 'headline', Q_BY: 'sub',
    MB_EYEBROW: 'eyebrow', MB_TITLE: 'headline', MB_CAPTION: 'sub',
    EV_EYEBROW: 'eyebrow', EV_TITLE: 'headline', EV_DATE: 'sub', EV_PLACE: 'spec', EV_RSVP: 'cta',
    ED_TITLE: 'headline', ED_META: 'sub',
    END_TAGLINE: 'headline',
};

/** A text layer with nothing in it — a slot the copy left empty. */
export const isBlankText = (o: fabric.Object | null | undefined) =>
    !!o && typeof (o as any).text === 'string' && !(o as any).text.trim();

export interface TemplateResult {
    background: string;
    objects: fabric.Object[];
}

export interface TemplateDef {
    id: string;
    nameKey: string;
    /** Category for the panel filter. */
    group: 'promo' | 'product' | 'story' | 'brand';
    build: (ctx: TemplateContext) => Promise<TemplateResult>;
}

// ─── Layout kit ─────────────────────────────────────────────────────────

interface Kit {
    W: number;
    H: number;
    /** Short side — the unit every size is a fraction of. */
    u: number;
    /** The margin. */
    m: number;
    portrait: boolean;
    landscape: boolean;
    head: string;
    headWeight: number;
    headUpper: boolean;
    body: string;
    brand: BrandInfo;
    T: (key: string) => string;
    text: (key: string, o: Omit<TextOpts, 'font'> & { font?: 'head' | 'body'; raw?: boolean }) => fabric.Textbox;
    pill: (key: string, o: Omit<PillOpts, 'font'> & { font?: 'head' | 'body'; raw?: boolean }) => fabric.Textbox;
    image: (index: number, box: Box, extra?: Parameters<typeof makeImage>[1]) => Promise<fabric.Object>;
    hero: () => Promise<fabric.Object>;
    mark: (box: Box, anchor: Anchor, color: string, opts?: { onDark?: boolean; size?: number }) => Promise<fabric.Object | null>;
    icon: (id: string, x: number, y: number, size: number, color: string) => Promise<fabric.Object | null>;
}

const h = (o: fabric.Object) => o.getScaledHeight();

/** Place objects top-down from y with gaps; returns the y below the last. */
function stackDown(objs: fabric.Object[], y: number, gaps: number[] | number): number {
    let cy = y;
    objs.forEach((o, i) => {
        if (isBlankText(o)) return;
        o.set({ top: cy + plateY(o) });
        o.setCoords();
        cy += visualH(o) + (Array.isArray(gaps) ? gaps[i] ?? 0 : gaps);
    });
    return cy;
}

/** Place objects so the last one ends at bottomY. */
function stackUp(objs: fabric.Object[], bottomY: number, gaps: number[] | number): number {
    const gapAt = (i: number) => (Array.isArray(gaps) ? gaps[i] ?? 0 : gaps);
    const items = objs.map((o, i) => ({ o, g: gapAt(i) })).filter((x) => !isBlankText(x.o));
    const total = items.reduce((acc, x, i) => acc + visualH(x.o) + (i < items.length - 1 ? x.g : 0), 0);
    const top = bottomY - total;
    stackDown(items.map((x) => x.o), top, items.map((x) => x.g));
    return top;
}

/** Height including a text plate's padding. */
const visualH = (o: fabric.Object) => h(o) + plateY(o) * 2;
const plateY = (o: fabric.Object) => (((o as any).bgPadY as number) || 0) * (o.scaleY || 1);
const plateX = (o: fabric.Object) => (((o as any).bgPadX as number) || 0) * (o.scaleX || 1);
const visualW = (o: fabric.Object) => o.getScaledWidth() + plateX(o) * 2;

/** Put a (possibly plated) object's visual left edge at x. */
const setVisualLeft = (o: fabric.Object, x: number) => { o.set({ left: x + plateX(o) }); o.setCoords(); };

async function createKit(ctx: TemplateContext): Promise<Kit> {
    const { art, brand, pair, t, images } = ctx;
    const W = art.w;
    const H = art.h;
    const u = Math.min(W, H);
    const m = Math.round(u * 0.074);
    const head = (ctx.useBrandFont && brand.font) || pair.heading;
    const headWeight = head === pair.heading ? pair.headingWeight : 400;
    const body = pair.body;
    await Promise.all([
        loadFont(head, headWeight),
        loadFont(head, headWeight, true),
        loadFont(body, 400),
        loadFont(body, 500),
    ]);
    const T = (key: string) => {
        if (!ctx.copy) return t(`DESIGN_EDITOR.TPL_${key}`);
        const slot = SLOTS[key];
        if (slot === 'keep') return t(`DESIGN_EDITOR.TPL_${key}`);
        return (slot && ctx.copy[slot]?.trim()) || '';
    };
    const headUpper = !!pair.headingUpper && head === pair.heading;

    const kit: Kit = {
        W, H, u, m,
        portrait: H / W > 1.15,
        landscape: W / H > 1.15,
        head, headWeight, headUpper, body, brand, T,
        text: (key, o) => {
            const isHead = o.font === 'head';
            return makeText(o.raw ? key : T(key), {
                ...o,
                font: isHead ? head : body,
                weight: o.weight ?? (isHead ? headWeight : 400),
                upper: o.upper ?? (isHead ? headUpper : false),
                fontRole: isHead ? 'heading' : 'body',
            });
        },
        pill: (key, o) => {
            const isHead = o.font === 'head';
            return makePill(o.raw ? key : T(key), {
                ...o,
                font: isHead ? head : body,
                weight: o.weight ?? 500,
                fontRole: isHead ? 'heading' : 'body',
            });
        },
        image: async (index, box, extra) => {
            const url = images[index] ?? images[0];
            if (!url) return placeholder(box, brand);
            try {
                return await makeImage(url, { ...box, ...(extra || {}) });
            } catch {
                return placeholder(box, brand);
            }
        },
        hero: async () => {
            const o = await kit.image(0, { x: 0, y: 0, w: W, h: H }, { role: 'background', name: 'Background' });
            if ((o as any).csRole === 'background') setLocked(o, true);
            return o;
        },
        mark: async (box, anchor, color, opts) => {
            if (brand.logoUrl && !opts?.onDark) {
                try { return await makeLogo(brand.logoUrl, box, anchor); } catch { /* fall through to wordmark */ }
            }
            const name = brand.name || T('BRAND');
            const size = opts?.size ?? u * 0.02;
            const wm = makeText(name, {
                x: box.x, y: box.y, w: box.w, size, font: body, weight: 500, color,
                spacing: 320, upper: true, name: 'Wordmark', fontRole: 'body',
                align: anchor.endsWith('l') ? 'left' : anchor.endsWith('r') ? 'right' : 'center',
            });
            const row = anchor[0];
            const top = row === 't' ? box.y : row === 'b' ? box.y + box.h - h(wm) : box.y + (box.h - h(wm)) / 2;
            wm.set({ top });
            wm.setCoords();
            return wm;
        },
        icon: async (id, x, y, size, color) => {
            const svg = iconSvg(id);
            if (!svg) return null;
            return makeSvgGroup(svg, { x, y, size, color, name: id });
        },
    };
    return kit;
}

function placeholder(box: Box, brand: BrandInfo): fabric.Object {
    const r = makeRect({ ...box, fill: contrast(brand.ground, '#D8D2C8') < 1.2 ? '#CFC8BC' : '#D8D2C8', role: 'image', name: 'Image placeholder' });
    return r;
}

/** Colour roles on the objects a template returns, for "Apply brand". */
function role<T extends fabric.Object>(o: T, r: 'ink' | 'ground' | 'accent' | 'onAccent' | 'white'): T {
    (o as any).csColorRole = r;
    return o;
}

const compact = (arr: Array<fabric.Object | null | undefined>) => arr.filter(Boolean) as fabric.Object[];

// ─── Templates ──────────────────────────────────────────────────────────

const newArrival: TemplateDef = {
    id: 'new-arrival',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_NEW_ARRIVAL',
    group: 'product',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m } = k;
        const hero = await k.hero();
        const scrim = makeScrim('bottom', W, H, 0.72, '#000000', k.portrait ? 0.55 : 0.68);
        const topScrim = makeScrim('top', W, H, 0.35, '#000000', 0.25);
        const mark = await k.mark({ x: m, y: m, w: u * 0.24, h: u * 0.07 }, 'tl', '#FFFFFF');
        const w = W - m * 2;
        const eyebrow = role(k.text('NA_EYEBROW', { x: m, y: 0, w, size: u * 0.019, weight: 500, color: '#FFFFFF', spacing: 340, upper: true, name: 'Eyebrow' }), 'white');
        const title = role(k.text('NA_TITLE', { font: 'head', x: m, y: 0, w: w * (k.landscape ? 0.6 : 0.92), size: u * (k.landscape ? 0.092 : 0.108), color: '#FFFFFF', lineHeight: 1, spacing: -12, name: 'Headline' }), 'white');
        const rule = makeLine(m, 0, m + u * 0.07, 0, { color: '#FFFFFF', width: Math.max(1.5, u * 0.0022), opacity: 0.8 });
        const sub = role(k.text('NA_SUB', { x: m, y: 0, w: w * 0.8, size: u * 0.025, color: '#FFFFFF', opacity: 0.88, lineHeight: 1.35, name: 'Subline' }), 'white');
        const bottom = H - m * (k.portrait ? 1.35 : 1.05);
        stackUp([eyebrow, title, rule, sub], bottom, [u * 0.03, u * 0.042, u * 0.034]);
        rule.set({ y1: rule.top, y2: rule.top } as any);
        return { background: '#111111', objects: compact([hero, scrim, topScrim, mark, eyebrow, title, rule, sub]) };
    },
};

const collection: TemplateDef = {
    id: 'collection',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_COLLECTION',
    group: 'product',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const ink = brand.ink;
        const objs: Array<fabric.Object | null> = [];
        const swatchFocus = [{ x: 0.22, y: 0.82 }, { x: 0.5, y: 0.9 }, { x: 0.8, y: 0.84 }];
        const swatchImage = (i: number, box: Box) => (ctx.images[i + 1]
            ? k.image(i + 1, box)
            : k.image(0, box, { focus: swatchFocus[i], zoom: 2.4 }));

        if (k.landscape) {
            const heroW = W * 0.5;
            objs.push(await k.image(0, { x: 0, y: 0, w: heroW, h: H }, { name: 'Hero' }));
            const x = heroW + m;
            const w = W - x - m;
            objs.push(await k.mark({ x, y: m, w: u * 0.22, h: u * 0.06 }, 'tl', ink));
            const eyebrow = role(k.text('COL_EYEBROW', { x, y: 0, w, size: u * 0.018, weight: 500, color: ink, spacing: 320, upper: true, name: 'Eyebrow' }), 'ink');
            const title = role(k.text('COL_TITLE', { font: 'head', x, y: 0, w, size: u * 0.085, color: ink, lineHeight: 1, name: 'Title' }), 'ink');
            const meta = role(k.text('COL_META', { x, y: 0, w, size: u * 0.02, color: ink, opacity: 0.68, name: 'Details' }), 'ink');
            const spec = role(k.pill('COL_SPEC', { x, y: 0, w, size: u * 0.019, color: ink, bg: 'rgba(0,0,0,0)', padX: u * 0.022, padY: u * 0.011, radius: u * 0.04, spacing: 60, outline: { color: rgba(ink, 0.28), width: Math.max(1, u * 0.0014) }, name: 'Spec' }), 'ink');
            setVisualLeft(spec, x);
            const after = stackDown([eyebrow, title, meta, spec], H * 0.22, [u * 0.03, u * 0.025, u * 0.04]);
            const gap = u * 0.022;
            const sw = Math.min((w - gap * 2) / 3, H - m - after - u * 0.09);
            const top = H - m - u * 0.05 - sw;
            for (let i = 0; i < 3; i++) {
                const sx = x + i * (sw + gap);
                objs.push(await swatchImage(i, { x: sx, y: top, w: sw, h: sw }));
                objs.push(role(k.text(`COL_SW${i + 1}`, { x: sx, y: top + sw + u * 0.018, w: sw, size: u * 0.016, weight: 500, color: ink, spacing: 220, upper: true, align: 'left', name: 'Swatch name' }), 'ink'));
            }
            objs.push(eyebrow, title, meta, spec);
            return { background: brand.ground, objects: compact(objs) };
        }

        // Portrait / square / story: header, inset hero, title row, swatch row.
        const markH = u * 0.055;
        objs.push(await k.mark({ x: m, y: m * 0.8, w: W - m * 2, h: markH }, 'tc', ink));
        const heroTop = m * 0.8 + markH + m * 0.55;
        const w = W - m * 2;
        // Measure the copy first; the swatches take the full width, and the
        // hero takes every pixel left over, so nothing floats in dead space.
        const title = role(k.text('COL_TITLE', { font: 'head', x: m, y: 0, w: w * 0.62, size: u * 0.066, color: ink, lineHeight: 1, name: 'Title' }), 'ink');
        const spec = role(k.text('COL_SPEC', { x: m, y: 0, w: w * 0.38, size: u * 0.02, weight: 500, color: ink, spacing: 120, align: 'right', name: 'Spec' }), 'ink');
        fitTextWidth(spec);
        const meta = role(k.text('COL_META', { x: m, y: 0, w, size: u * 0.018, color: ink, opacity: 0.66, spacing: 40, name: 'Details' }), 'ink');
        const gap = u * 0.022;
        const labelH = u * 0.016 + h(k.text('COL_SW1', { x: 0, y: 0, w: W, size: u * 0.015, weight: 500, upper: true }));
        const copyH = m * 0.5 + h(title) + u * 0.016 + h(meta) + m * 0.5;
        const bottomPad = m * 0.85;
        let sw = (w - gap * 2) / 3;
        let heroH = H - heroTop - copyH - sw - labelH - bottomPad;
        if (heroH < H * 0.3) {
            heroH = H * 0.3;
            sw = Math.max(u * 0.08, H - heroTop - copyH - labelH - bottomPad - heroH);
        }
        objs.push(await k.image(0, { x: m, y: heroTop, w, h: heroH }, { name: 'Hero' }));
        const titleTop = heroTop + heroH + m * 0.5;
        title.set({ top: titleTop });
        spec.set({ left: W - m - (spec.width || 0), top: titleTop + h(title) - h(spec) - u * 0.004 });
        meta.set({ top: titleTop + h(title) + u * 0.016 });
        const rowTop = (meta.top || 0) + h(meta) + m * 0.5;
        const rowW = sw * 3 + gap * 2;
        const rowX = (W - rowW) / 2;
        for (let i = 0; i < 3; i++) {
            const sx = rowX + i * (sw + gap);
            objs.push(await swatchImage(i, { x: sx, y: rowTop, w: sw, h: sw }));
            objs.push(role(k.text(`COL_SW${i + 1}`, { x: sx, y: rowTop + sw + u * 0.016, w: sw, size: u * 0.015, weight: 500, color: ink, spacing: 220, upper: true, align: 'center', name: 'Swatch name' }), 'ink'));
        }
        objs.push(title, spec, meta);
        return { background: brand.ground, objects: compact(objs) };
    },
};

const spotlight: TemplateDef = {
    id: 'spotlight',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_SPOTLIGHT',
    group: 'product',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const ink = brand.ink;
        const objs: Array<fabric.Object | null> = [];
        const x = k.landscape ? W * 0.55 + m : m;
        const w = k.landscape ? W - x - m : W - m * 2;
        const accentOk = contrast(brand.accent, brand.ground) >= 2.5;
        const eyebrow = role(k.text('SP_EYEBROW', { x, y: 0, w, size: u * 0.018, weight: 500, color: accentOk ? brand.accent : ink, spacing: 320, upper: true, name: 'Eyebrow' }), accentOk ? 'accent' : 'ink');
        const title = role(k.text('SP_TITLE', { font: 'head', x, y: 0, w, size: u * 0.074, color: ink, lineHeight: 1, name: 'Product name' }), 'ink');
        const desc = role(k.text('SP_DESC', { x, y: 0, w: Math.min(w, u * 0.8), size: u * 0.023, color: ink, opacity: 0.78, lineHeight: 1.45, name: 'Description' }), 'ink');
        const chips = ['SP_CHIP1', 'SP_CHIP2', 'SP_CHIP3'].map((key) => role(k.pill(key, {
            x: 0, y: 0, w: W, size: u * 0.018, color: ink, bg: 'rgba(0,0,0,0)', padX: u * 0.022, padY: u * 0.012, radius: u * 0.04, spacing: 60,
            outline: { color: rgba(ink, 0.28), width: Math.max(1, u * 0.0014) }, name: 'Spec chip',
        }), 'ink'));
        const gaps = [u * 0.022, u * 0.024, u * 0.036];
        const textH = visualH(eyebrow) + visualH(title) + visualH(desc) + visualH(chips[0]) + gaps.reduce((a, g) => a + g, 0);
        let top: number;
        if (k.landscape) {
            objs.push(await k.image(0, { x: 0, y: 0, w: W * 0.55, h: H }, { name: 'Product' }));
            top = Math.max(m, (H - textH) / 2 - u * 0.03);
        } else {
            // The picture takes whatever the copy and the logo row leave.
            const imgH = Math.max(H * 0.42, Math.min(H * 0.66, H - (m * 0.85 + textH + m * 0.8 + u * 0.055 + m)));
            objs.push(await k.image(0, { x: 0, y: 0, w: W, h: imgH }, { name: 'Product' }));
            top = imgH + m * 0.85;
        }
        const chipsY = stackDown([eyebrow, title, desc], top, gaps) ;
        // Spec chips in a row, wrapping if the column is narrow.
        let cx = x;
        let cy = chipsY;
        for (const chip of chips) {
            if (cx + visualW(chip) > x + w && cx > x) { cx = x; cy += visualH(chip) + u * 0.014; }
            setVisualLeft(chip, cx);
            chip.set({ top: cy + plateY(chip) });
            chip.setCoords();
            cx += visualW(chip) + u * 0.012;
            objs.push(chip);
        }
        objs.push(eyebrow, title, desc);
        objs.push(await k.mark({ x: W - m - u * 0.2, y: H - m - u * 0.055, w: u * 0.2, h: u * 0.055 }, 'br', ink));
        return { background: brand.ground, objects: compact(objs) };
    },
};

const testimonial: TemplateDef = {
    id: 'quote',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_QUOTE',
    group: 'brand',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const ink = brand.ink;
        const d = u * 0.2;
        const avatar = await k.image(0, { x: (W - d) / 2, y: 0, w: d, h: d }, { mask: 'circle', name: 'Photo' });
        const markSize = u * 0.13;
        const mark = role(k.text('“', { font: 'head', raw: true, x: 0, y: 0, w: W, size: markSize, color: brand.accent, align: 'center', lineHeight: 1, name: 'Quote mark' }), 'accent');
        const qw = Math.min(W - m * 2.4, u * 0.86);
        const quote = role(k.text('Q_TEXT', { font: 'head', x: (W - qw) / 2, y: 0, w: qw, size: u * 0.056, italic: true, color: ink, align: 'center', lineHeight: 1.2, name: 'Quote' }), 'ink');
        const by = role(k.text('Q_BY', { x: m, y: 0, w: W - m * 2, size: u * 0.017, weight: 500, color: ink, spacing: 260, upper: true, align: 'center', opacity: 0.8, name: 'Attribution' }), 'ink');
        const items = [avatar, mark, quote, by];
        // A quotation mark's box is a full line tall with the glyph at the top:
        // tuck the quote up under it.
        const gaps = [u * 0.055, -markSize * 0.55, u * 0.045];
        const total = items.reduce((a, o) => a + visualH(o), 0) + gaps.reduce((a, g) => a + g, 0);
        const top = Math.max(m, (H - total) / 2 - u * 0.03);
        stackDown(items, top, gaps);
        const logo = await k.mark({ x: (W - u * 0.2) / 2, y: H - m - u * 0.05, w: u * 0.2, h: u * 0.05 }, 'bc', ink, { size: u * 0.016 });
        return { background: brand.ground, objects: compact([avatar, mark, quote, by, logo]) };
    },
};

const sale: TemplateDef = {
    id: 'sale',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_SALE',
    group: 'promo',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const ink = brand.ink;
        const hero = await k.hero();
        const logo = await k.mark({ x: m, y: m, w: u * 0.22, h: u * 0.065 }, 'tl', '#FFFFFF');
        const pad = u * 0.06;
        const cardW = k.landscape ? W * 0.44 : k.portrait ? W - m * 2 : W * 0.66;
        const innerW = cardW - pad * 2;
        const accentOk = contrast(brand.accent, brand.ground) >= 2.5;
        const eyebrow = role(k.text('SALE_EYEBROW', { x: 0, y: 0, w: innerW, size: u * 0.018, weight: 500, color: accentOk ? brand.accent : ink, spacing: 320, upper: true, name: 'Eyebrow' }), accentOk ? 'accent' : 'ink');
        const title = role(k.text('SALE_TITLE', { font: 'head', x: 0, y: 0, w: innerW, size: u * 0.088, color: ink, lineHeight: 1, name: 'Offer' }), 'ink');
        const sub = role(k.text('SALE_SUB', { x: 0, y: 0, w: innerW, size: u * 0.022, color: ink, opacity: 0.75, lineHeight: 1.4, name: 'Details' }), 'ink');
        const cta = role(k.pill('SALE_CTA', { x: 0, y: 0, w: W, size: u * 0.021, color: brand.onAccent, bg: brand.accent, padX: u * 0.032, padY: u * 0.017, radius: u * 0.05, spacing: 40, name: 'Button' }), 'onAccent');
        const inner = [eyebrow, title, sub, cta];
        const gaps = [u * 0.022, u * 0.022, u * 0.04];
        const innerH = inner.reduce((a, o) => a + visualH(o), 0) + gaps.reduce((a, g) => a + g, 0);
        const cardH = innerH + pad * 2;
        const cardX = m;
        const cardY = H - m - cardH;
        const card = role(makeRect({ x: cardX, y: cardY, w: cardW, h: cardH, fill: brand.ground, rx: u * 0.012, name: 'Card' }), 'ground');
        inner.forEach((o) => setVisualLeft(o, cardX + pad));
        stackDown(inner, cardY + pad, gaps);
        // The badge: a plain disc, the offer in the display face.
        const d = u * 0.21;
        const bx = W - m - d;
        const by = k.portrait ? m * 1.2 : m;
        const hasBadge = !!k.T('SALE_BADGE');
        const disc = role(new fabric.Circle({ left: bx, top: by, radius: d / 2, fill: brand.accent } as any), 'accent');
        tag(disc, { role: 'shape', name: 'Badge' });
        const pct = role(k.text('SALE_BADGE', { font: 'head', x: bx, y: 0, w: d, size: u * 0.064, color: brand.onAccent, align: 'center', lineHeight: 1, name: 'Badge value' }), 'onAccent');
        const ends = role(k.text('SALE_BADGE_SUB', { x: bx, y: 0, w: d, size: u * 0.0125, weight: 500, color: brand.onAccent, align: 'center', spacing: 220, upper: true, name: 'Badge caption' }), 'onAccent');
        const bh = h(pct) + u * 0.008 + h(ends);
        stackDown([pct, ends], by + (d - bh) / 2, u * 0.008);
        return { background: '#111111', objects: compact([hero, logo, card, ...inner, ...(hasBadge ? [disc, pct, ends] : [])]) };
    },
};

const beforeAfter: TemplateDef = {
    id: 'before-after',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_BEFORE_AFTER',
    group: 'product',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const side = W >= H * 0.9;
        const a: Box = side ? { x: 0, y: 0, w: W / 2, h: H } : { x: 0, y: 0, w: W, h: H / 2 };
        const b: Box = side ? { x: W / 2, y: 0, w: W / 2, h: H } : { x: 0, y: H / 2, w: W, h: H / 2 };
        const before = await k.image(ctx.images[1] ? 1 : 0, a, { name: 'Before' });
        if (!ctx.images[1] && before.type === 'image') {
            // Only one picture: show the "before" desaturated so the pair reads
            // at a glance until the vendor drops in the real one.
            const img = before as fabric.Image;
            img.filters = [new fabric.Image.filters.Grayscale(), new fabric.Image.filters.Brightness({ brightness: -0.04 } as any)];
            img.applyFilters();
        }
        const after = await k.image(0, b, { name: 'After' });
        const lw = Math.max(3, u * 0.004);
        const divider = side
            ? makeLine(W / 2, 0, W / 2, H, { color: '#FFFFFF', width: lw, role: 'divider', name: 'Divider' })
            : makeLine(0, H / 2, W, H / 2, { color: '#FFFFFF', width: lw, role: 'divider', name: 'Divider' });
        const knobD = u * 0.075;
        const knob = new fabric.Circle({ left: W / 2 - knobD / 2, top: H / 2 - knobD / 2, radius: knobD / 2, fill: '#FFFFFF' } as any);
        tag(knob, { role: 'shape', name: 'Handle' });
        const icon = await k.icon('compare', W / 2 - knobD * 0.28, H / 2 - knobD * 0.28, knobD * 0.56, brand.ink);
        if (icon && !side) icon.set({ angle: 90, left: W / 2 + knobD * 0.28 });
        const inset = u * 0.04;
        const lb = role(k.pill('BA_BEFORE', { x: 0, y: 0, w: W, size: u * 0.016, color: brand.ink, bg: '#FFFFFF', padX: u * 0.018, padY: u * 0.009, radius: u * 0.03, spacing: 240, upper: true, name: 'Label' }), 'ink');
        const la = role(k.pill('BA_AFTER', { x: 0, y: 0, w: W, size: u * 0.016, color: brand.onAccent, bg: brand.accent, padX: u * 0.018, padY: u * 0.009, radius: u * 0.03, spacing: 240, upper: true, name: 'Label' }), 'onAccent');
        setVisualLeft(lb, a.x + inset); lb.set({ top: a.y + inset + plateY(lb) });
        setVisualLeft(la, b.x + inset); la.set({ top: b.y + inset + plateY(la) });
        const scrim = makeScrim('bottom', W, H, 0.7, '#000000', 0.36);
        const caption = role(k.text('BA_CAPTION', { font: 'head', x: m, y: 0, w: W - m * 2, size: u * 0.05, color: '#FFFFFF', align: 'center', lineHeight: 1.05, name: 'Caption' }), 'white');
        caption.set({ top: H - m - h(caption) });
        return { background: '#111111', objects: compact([before, after, divider, knob, icon, lb, la, scrim, caption]) };
    },
};

const storyCta: TemplateDef = {
    id: 'story-cta',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_STORY',
    group: 'story',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const hero = await k.hero();
        const top = makeScrim('top', W, H, 0.42, '#000000', 0.28);
        const bottom = makeScrim('bottom', W, H, 0.82, '#000000', k.portrait ? 0.62 : 0.78);
        // Story UI covers the top ~12% and bottom ~15%: keep marks and the CTA clear of it.
        const safeTop = k.portrait ? H * 0.075 : m;
        const safeBottom = k.portrait ? H * 0.11 : m * 1.1;
        const mark = await k.mark({ x: m, y: safeTop, w: W - m * 2, h: u * 0.065 }, 'tc', '#FFFFFF');
        const w = W - m * 2;
        const eyebrow = role(k.text('ST_EYEBROW', { x: m, y: 0, w, size: u * 0.02, weight: 500, color: '#FFFFFF', spacing: 360, upper: true, align: 'center', name: 'Eyebrow' }), 'white');
        const title = role(k.text('ST_TITLE', { font: 'head', x: m + w * 0.04, y: 0, w: w * 0.92, size: u * 0.098, color: '#FFFFFF', align: 'center', lineHeight: 1.02, name: 'Headline' }), 'white');
        const sub = role(k.text('ST_SUB', { x: m, y: 0, w, size: u * 0.026, color: '#FFFFFF', opacity: 0.88, align: 'center', name: 'Subline' }), 'white');
        const cta = role(k.pill('ST_CTA', { x: 0, y: 0, w: W, size: u * 0.025, color: brand.ink, bg: '#FFFFFF', padX: u * 0.045, padY: u * 0.022, radius: u * 0.08, spacing: 30, name: 'Button' }), 'ink');
        const link = role(k.text('ST_LINK', { x: m, y: 0, w, size: u * 0.016, weight: 500, color: '#FFFFFF', opacity: 0.8, align: 'center', spacing: 260, upper: true, name: 'Link hint' }), 'white');
        stackUp([eyebrow, title, sub, cta, link], H - safeBottom, [u * 0.03, u * 0.03, u * 0.06, u * 0.03]);
        setVisualLeft(cta, (W - visualW(cta)) / 2);
        return { background: '#111111', objects: compact([hero, top, bottom, mark, eyebrow, title, sub, cta, link]) };
    },
};

const callouts: TemplateDef = {
    id: 'callouts',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_CALLOUTS',
    group: 'product',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const hero = await k.hero();
        const scrim = makeScrim('top', W, H, 0.6, '#000000', 0.42);
        const w = W - m * 2;
        const eyebrow = role(k.text('CO_EYEBROW', { x: m, y: 0, w, size: u * 0.018, weight: 500, color: '#FFFFFF', spacing: 320, upper: true, name: 'Eyebrow' }), 'white');
        const title = role(k.text('CO_TITLE', { font: 'head', x: m, y: 0, w: w * 0.75, size: u * 0.072, color: '#FFFFFF', lineHeight: 1.02, name: 'Headline' }), 'white');
        stackDown([eyebrow, title], m, u * 0.024);
        const points = [
            { x: 0.3, y: 0.62, key: 'CO_C1' },
            { x: 0.72, y: 0.5, key: 'CO_C2' },
            { x: 0.58, y: 0.82, key: 'CO_C3' },
        ];
        const out: fabric.Object[] = [hero, scrim, eyebrow, title];
        const r = u * 0.012;
        for (const p of points) {
            const px = W * p.x;
            const py = H * p.y;
            const toLeft = p.x > 0.55;
            if (!k.T(p.key)) continue;
            const label = role(k.pill(p.key, { x: 0, y: 0, w: W, size: u * 0.018, color: brand.ink, bg: '#FFFFFF', padX: u * 0.02, padY: u * 0.011, radius: u * 0.006, name: 'Callout' }), 'ink');
            const lx = toLeft ? px - u * 0.09 - visualW(label) : px + u * 0.09;
            const ly = py - u * 0.07;
            setVisualLeft(label, Math.max(m * 0.5, Math.min(W - m * 0.5 - visualW(label), lx)));
            label.set({ top: ly - visualH(label) / 2 + plateY(label) });
            const endX = toLeft ? (label.left || 0) + (label.width || 0) + plateX(label) : (label.left || 0) - plateX(label);
            const leader = makeLine(px, py, endX, ly, { color: '#FFFFFF', width: Math.max(1.5, u * 0.002), name: 'Leader' });
            const ring = new fabric.Circle({ left: px - r * 2, top: py - r * 2, radius: r * 2, fill: 'rgba(255,255,255,0.28)', stroke: '#FFFFFF', strokeWidth: Math.max(1.5, u * 0.002), strokeUniform: true } as any);
            const dot = new fabric.Circle({ left: px - r * 0.8, top: py - r * 0.8, radius: r * 0.8, fill: '#FFFFFF' } as any);
            tag(ring, { role: 'shape', name: 'Marker' });
            tag(dot, { role: 'shape', name: 'Marker' });
            out.push(leader, ring, dot, label);
        }
        const mark = await k.mark({ x: W - m - u * 0.2, y: H - m - u * 0.055, w: u * 0.2, h: u * 0.055 }, 'br', '#FFFFFF');
        if (mark) out.push(mark);
        return { background: '#111111', objects: out };
    },
};

const endCard: TemplateDef = {
    id: 'end-card',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_END_CARD',
    group: 'brand',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const ink = brand.ink;
        let logo: fabric.Object | null = null;
        const boxW = Math.min(W * 0.46, u * 0.5);
        const boxH = u * 0.2;
        if (brand.logoUrl) {
            try { logo = await makeLogo(brand.logoUrl, { x: (W - boxW) / 2, y: 0, w: boxW, h: boxH }, 'c'); } catch { logo = null; }
        }
        if (!logo) {
            logo = role(k.text(brand.name || k.T('BRAND'), { font: 'head', raw: true, x: m, y: 0, w: W - m * 2, size: u * 0.1, color: ink, align: 'center', lineHeight: 1, name: 'Brand name' }), 'ink');
        }
        const rule = role(makeLine(W / 2 - u * 0.035, 0, W / 2 + u * 0.035, 0, { color: brand.accent, width: Math.max(1.5, u * 0.0025) }), 'accent');
        const tagline = role(k.text('END_TAGLINE', { font: 'head', x: m, y: 0, w: W - m * 2, size: u * 0.04, italic: true, color: ink, align: 'center', lineHeight: 1.2, name: 'Tagline' }), 'ink');
        const items = [logo, rule, tagline];
        const gaps = [u * 0.06, u * 0.05];
        const total = items.reduce((a, o) => a + visualH(o), 0) + gaps.reduce((a, g) => a + g, 0);
        stackDown(items, (H - total) / 2 - u * 0.02, gaps);
        rule.set({ y1: rule.top, y2: rule.top } as any);
        const url = role(k.text('END_URL', { x: m, y: 0, w: W - m * 2, size: u * 0.017, weight: 500, color: ink, spacing: 300, upper: true, align: 'center', opacity: 0.7, name: 'Website' }), 'ink');
        url.set({ top: H - m - h(url) });
        return { background: brand.ground, objects: compact([logo, rule, tagline, url]) };
    },
};

const moodboard: TemplateDef = {
    id: 'moodboard',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_MOODBOARD',
    group: 'brand',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const ink = brand.ink;
        const w = W - m * 2;
        const eyebrow = role(k.text('MB_EYEBROW', { x: m, y: 0, w: w * 0.6, size: u * 0.017, weight: 500, color: ink, spacing: 320, upper: true, name: 'Eyebrow' }), 'ink');
        const title = role(k.text('MB_TITLE', { font: 'head', x: m, y: 0, w: w * 0.62, size: u * 0.066, color: ink, lineHeight: 1, name: 'Title' }), 'ink');
        const headerBottom = stackDown([eyebrow, title], m, u * 0.018);
        // Palette: sampled from the hero when the browser lets us read it.
        let palette: string[] = [];
        if (ctx.images[0]) {
            try { palette = extractPalette(await loadImageElement(ctx.images[0]), 4); } catch { palette = []; }
        }
        if (palette.length < 4) palette = [brand.ink, brand.accent, '#B9AE9C', '#E7E0D4'];
        const dot = u * 0.05;
        const dgap = u * 0.012;
        const swatches = palette.slice(0, 4).map((c, i) => {
            const s = new fabric.Circle({ left: W - m - (4 - i) * dot - (3 - i) * dgap, top: headerBottom - dot - u * 0.004, radius: dot / 2, fill: c, stroke: rgba('#000000', 0.08), strokeWidth: 1, strokeUniform: true } as any);
            tag(s, { role: 'shape', name: 'Swatch' });
            return s;
        });
        const g = u * 0.016;
        const caption = role(k.text('MB_CAPTION', { x: m, y: 0, w, size: u * 0.017, color: ink, opacity: 0.7, spacing: 80, name: 'Caption' }), 'ink');
        const gridTop = headerBottom + m * 0.55;
        const gridBottom = H - m - h(caption) - u * 0.03;
        caption.set({ top: gridBottom + u * 0.03 });
        const gh = gridBottom - gridTop;
        const slots: Box[] = k.portrait
            ? [
                { x: m, y: gridTop, w, h: gh * 0.58 - g / 2 },
                { x: m, y: gridTop + gh * 0.58 + g / 2, w: (w - g) / 2, h: gh * 0.42 - g / 2 },
                { x: m + (w + g) / 2, y: gridTop + gh * 0.58 + g / 2, w: (w - g) / 2, h: gh * 0.42 - g / 2 },
            ]
            : [
                { x: m, y: gridTop, w: w * 0.6 - g / 2, h: gh },
                { x: m + w * 0.6 + g / 2, y: gridTop, w: w * 0.4 - g / 2, h: gh / 2 - g / 2 },
                { x: m + w * 0.6 + g / 2, y: gridTop + gh / 2 + g / 2, w: w * 0.4 - g / 2, h: gh / 2 - g / 2 },
            ];
        const pics: fabric.Object[] = [];
        for (let i = 0; i < slots.length; i++) {
            const box = slots[i];
            if (ctx.images[i]) pics.push(await k.image(i, box));
            else pics.push(await k.image(0, box, { focus: i === 1 ? { x: 0.3, y: 0.85 } : { x: 0.75, y: 0.4 }, zoom: i === 1 ? 2.2 : 1.7 }));
        }
        return { background: brand.ground, objects: compact([...pics, eyebrow, title, ...swatches, caption]) };
    },
};

const tipSlide: TemplateDef = {
    id: 'tip',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_TIP',
    group: 'story',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const ink = brand.ink;
        const accentOk = contrast(brand.accent, brand.ground) >= 2.5;
        const textW = k.landscape ? W * 0.5 - m * 1.5 : W - m * 2;
        const num = role(k.text('01', { font: 'head', raw: true, x: m, y: m * 0.9, w: u * 0.4, size: u * 0.13, color: accentOk ? brand.accent : ink, lineHeight: 0.9, name: 'Number' }), accentOk ? 'accent' : 'ink');
        fitTextWidth(num);
        const count = role(k.text('TIP_COUNT', { x: m + (num.width || 0) + u * 0.014, y: 0, w: u * 0.2, size: u * 0.02, color: ink, opacity: 0.5, name: 'Count' }), 'ink');
        count.set({ top: (num.top || 0) + h(num) - h(count) - u * 0.008 });
        const eyebrow = role(k.text('TIP_EYEBROW', { x: m, y: 0, w: textW, size: u * 0.017, weight: 500, color: ink, spacing: 320, upper: true, name: 'Eyebrow' }), 'ink');
        const title = role(k.text('TIP_TITLE', { font: 'head', x: m, y: 0, w: textW, size: u * 0.062, color: ink, lineHeight: 1.04, name: 'Headline' }), 'ink');
        const body = role(k.text('TIP_BODY', { x: m, y: 0, w: Math.min(textW, u * 0.82), size: u * 0.023, color: ink, opacity: 0.78, lineHeight: 1.48, name: 'Body' }), 'ink');
        const textBottom = stackDown([eyebrow, title, body], (num.top || 0) + h(num) + u * 0.05, [u * 0.022, u * 0.026]);
        const footY = H - m - u * 0.03;
        const word = await k.mark({ x: m, y: footY, w: W * 0.4, h: u * 0.03 }, 'bl', ink, { onDark: true, size: u * 0.016 });
        const swipe = role(k.text('TIP_SWIPE', { x: 0, y: 0, w: W, size: u * 0.016, weight: 500, color: ink, spacing: 260, upper: true, name: 'Swipe' }), 'ink');
        fitTextWidth(swipe);
        const iconSize = u * 0.026;
        swipe.set({ left: W - m - (swipe.width || 0) - iconSize - u * 0.01, top: footY + u * 0.03 - h(swipe) });
        const arrow = await k.icon('arrow-right', W - m - iconSize, (swipe.top || 0) + h(swipe) / 2 - iconSize / 2, iconSize, ink);
        if (arrow) role(arrow, 'ink');
        let pic: fabric.Object;
        if (k.landscape) {
            pic = await k.image(0, { x: W * 0.5, y: m, w: W * 0.5 - m, h: H - m * 2 - u * 0.06 }, { mask: 'rounded', maskRadius: u * 0.016 });
        } else {
            const top = textBottom + u * 0.02;
            const bottom = footY - u * 0.05;
            pic = await k.image(0, { x: m, y: top, w: W - m * 2, h: Math.max(u * 0.1, bottom - top) }, { mask: 'rounded', maskRadius: u * 0.016 });
        }
        return { background: brand.ground, objects: compact([pic, num, count, eyebrow, title, body, word, swipe, arrow]) };
    },
};

const event: TemplateDef = {
    id: 'event',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_EVENT',
    group: 'promo',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const dark = brand.ink;
        const light = brand.ground;
        const accent = contrast(brand.accent, dark) >= 3 ? brand.accent : light;
        const out: Array<fabric.Object | null> = [];
        let colX: number; let colW: number; let y: number;
        if (k.landscape) {
            const aw = W * 0.34;
            out.push(await k.image(0, { x: m * 1.4, y: m, w: aw, h: H - m * 2 }, { mask: 'arch', name: 'Photo' }));
            colX = m * 1.4 + aw + m * 1.4; colW = W - colX - m; y = H * 0.2;
        } else {
            const aw = W * (k.portrait ? 0.56 : 0.44);
            const ah = H * (k.portrait ? 0.4 : 0.42);
            out.push(await k.image(0, { x: (W - aw) / 2, y: m * 1.1, w: aw, h: ah }, { mask: 'arch', name: 'Photo' }));
            colX = m; colW = W - m * 2; y = m * 1.1 + ah + m * 0.7;
        }
        const align = k.landscape ? 'left' : 'center';
        const eyebrow = role(k.text('EV_EYEBROW', { x: colX, y: 0, w: colW, size: u * 0.018, weight: 500, color: accent, spacing: 360, upper: true, align, name: 'Eyebrow' }), accent === light ? 'ground' : 'accent');
        const title = role(k.text('EV_TITLE', { font: 'head', x: colX, y: 0, w: colW, size: u * 0.08, color: light, align, lineHeight: 1.02, name: 'Title' }), 'ground');
        const after = stackDown([eyebrow, title], y, u * 0.024) + u * 0.035;
        const rows: Array<[string, string]> = [['calendar', 'EV_DATE'], ['map-pin', 'EV_PLACE']];
        let ry = after;
        const iconSize = u * 0.026;
        for (const [iconId, key] of rows) {
            if (!k.T(key)) continue;
            const txt = role(k.text(key, { x: 0, y: ry, w: colW, size: u * 0.021, color: light, opacity: 0.9, name: 'Detail' }), 'ground');
            fitTextWidth(txt);
            const rowW = iconSize + u * 0.014 + (txt.width || 0);
            const startX = align === 'center' ? (W - rowW) / 2 : colX;
            txt.set({ left: startX + iconSize + u * 0.014 });
            const ic = await k.icon(iconId, startX, ry + (h(txt) - iconSize) / 2, iconSize, accent);
            if (ic) role(ic, accent === light ? 'ground' : 'accent');
            out.push(ic, txt);
            ry += h(txt) + u * 0.018;
        }
        const rsvp = role(k.pill('EV_RSVP', { x: 0, y: 0, w: W, size: u * 0.019, color: dark, bg: light, padX: u * 0.034, padY: u * 0.016, radius: u * 0.06, spacing: 80, name: 'Button' }), 'ink');
        rsvp.set({ top: ry + u * 0.03 + plateY(rsvp) });
        setVisualLeft(rsvp, align === 'center' ? (W - visualW(rsvp)) / 2 : colX);
        const word = await k.mark({ x: m, y: H - m - u * 0.03, w: W - m * 2, h: u * 0.03 }, k.landscape ? 'br' : 'bc', light, { onDark: true, size: u * 0.016 });
        if (word) role(word, 'ground');
        out.push(eyebrow, title, rsvp, word);
        return { background: dark, objects: compact(out) };
    },
};

const editorial: TemplateDef = {
    id: 'editorial',
    nameKey: 'DESIGN_EDITOR.TPL_NAME_EDITORIAL',
    group: 'brand',
    build: async (ctx) => {
        const k = await createKit(ctx);
        const { W, H, u, m, brand } = k;
        const ink = brand.ink;
        const capH = u * 0.15;
        const pic = await k.image(0, { x: m, y: m, w: W - m * 2, h: H - m * 2 - capH }, { name: 'Photo' });
        const top = H - m - capH + u * 0.045;
        const title = role(k.text('ED_TITLE', { font: 'head', x: m, y: top, w: (W - m * 2) * 0.66, size: u * 0.042, color: ink, lineHeight: 1.05, name: 'Title' }), 'ink');
        const meta = role(k.text('ED_META', { x: m, y: top + h(title) + u * 0.012, w: (W - m * 2) * 0.66, size: u * 0.017, color: ink, opacity: 0.66, spacing: 60, name: 'Details' }), 'ink');
        const mark = await k.mark({ x: W - m - u * 0.2, y: top, w: u * 0.2, h: h(title) + u * 0.012 + h(meta) }, 'cr', ink, { size: u * 0.015 });
        return { background: '#FFFFFF', objects: compact([pic, title, meta, mark]) };
    },
};

export const TEMPLATES: TemplateDef[] = [
    newArrival,
    collection,
    spotlight,
    sale,
    storyCta,
    beforeAfter,
    callouts,
    tipSlide,
    testimonial,
    moodboard,
    event,
    editorial,
    endCard,
];

export const TEMPLATE_IDS = TEMPLATES.map((t) => t.id);

export function findTemplate(id: string): TemplateDef | undefined {
    return TEMPLATES.find((t) => t.id === id);
}

/** For callers that need the text width helper with the same semantics. */
export { textWidth };
